/**
 * Config-driven "extra fields" that let one generic booking form serve every target
 * industry (barber, auto repair, real estate, roofing, veterinary, dental) without an
 * industry-specific screen for each -- see docs/architecture.md section 15. Industry
 * differences live entirely in this field-list DATA (per service, in `services.intake_fields`),
 * never in per-industry UI code.
 */

export type IntakeFieldType =
  | "short_text"
  | "long_text"
  | "single_select"
  | "multi_select"
  | "phone"
  | "email"
  | "date"
  | "address"
  | "number"
  | "boolean"
  | "identifier";

export interface IntakeFieldDef {
  key: string;
  label: string;
  type: IntakeFieldType;
  /** Required for single_select / multi_select. */
  options?: string[];
  required?: boolean;
  help_text?: string;
}

/**
 * Suggested starting field lists per `businesses.industry` value, from a review of how
 * established booking tools in each vertical structure intake (Booksy/Vagaro/Squire for
 * salons, Shopmonkey/Tekmetric for auto repair, ShowingTime/Zoho for real estate,
 * JobNimbus/ServiceTitan for trades, Vetstoria/PetDesk for vet, NexHealth/Dentrix for
 * dental). Staff can edit/remove/add to these per service in Services.tsx -- this is a
 * one-click starting point, not a fixed schema.
 */
export const INDUSTRY_INTAKE_TEMPLATES: Record<string, IntakeFieldDef[]> = {
  barber: [
    { key: "stylist_preference", label: "Stylist preference", type: "short_text" },
    { key: "style_notes", label: "Style / colour notes", type: "long_text" },
    { key: "allergy_notes", label: "Allergy / chemical sensitivity notes", type: "long_text" },
    { key: "new_client", label: "New client", type: "boolean" },
  ],
  salon: [
    { key: "stylist_preference", label: "Stylist preference", type: "short_text" },
    { key: "style_notes", label: "Style / colour notes", type: "long_text" },
    { key: "allergy_notes", label: "Allergy / chemical sensitivity notes", type: "long_text" },
    { key: "new_client", label: "New client", type: "boolean" },
  ],
  auto_repair: [
    { key: "vehicle_make_model_year", label: "Vehicle make / model / year", type: "short_text", required: true },
    { key: "vehicle_vin_or_plate", label: "VIN or licence plate", type: "identifier" },
    { key: "mileage", label: "Current mileage", type: "number" },
    { key: "issue_description", label: "What's wrong / reason for visit", type: "long_text", required: true },
    {
      key: "drop_off_preference",
      label: "Drop-off or wait in shop",
      type: "single_select",
      options: ["Drop off", "Wait in shop", "Mobile / on-site"],
    },
  ],
  real_estate: [
    { key: "property_address", label: "Property address", type: "address", required: true },
    {
      key: "appointment_type",
      label: "Appointment type",
      type: "single_select",
      options: ["Showing", "Open house", "Inspection", "Appraisal", "Walkthrough"],
    },
    { key: "access_instructions", label: "Access / lockbox instructions", type: "long_text" },
    { key: "attendee_count", label: "Number of attendees", type: "number" },
  ],
  roofing: [
    { key: "property_address", label: "Property address", type: "address", required: true },
    {
      key: "job_type",
      label: "Job type",
      type: "single_select",
      options: ["Inspection", "Repair", "Replacement", "Estimate"],
    },
    { key: "issue_description", label: "Issue description", type: "long_text" },
    { key: "insurance_claim", label: "Insurance claim involved", type: "boolean" },
    { key: "insurance_claim_number", label: "Insurance claim / policy number", type: "identifier" },
    { key: "urgent", label: "Urgent / emergency", type: "boolean" },
  ],
  trades: [
    { key: "property_address", label: "Property address", type: "address", required: true },
    { key: "job_type", label: "Job type", type: "short_text" },
    { key: "issue_description", label: "Issue description", type: "long_text" },
    { key: "urgent", label: "Urgent / emergency", type: "boolean" },
  ],
  veterinary: [
    { key: "pet_name", label: "Pet name", type: "short_text", required: true },
    { key: "pet_species", label: "Species", type: "single_select", options: ["Dog", "Cat", "Bird", "Rabbit", "Other"] },
    { key: "pet_breed", label: "Breed", type: "short_text" },
    { key: "pet_age_or_dob", label: "Age / date of birth", type: "short_text" },
    { key: "pet_weight", label: "Weight", type: "short_text" },
    { key: "reason_for_visit", label: "Reason for visit", type: "long_text", required: true },
    { key: "new_patient", label: "New patient", type: "boolean" },
  ],
  dental: [
    { key: "new_patient", label: "New patient", type: "boolean", required: true },
    { key: "procedure_type", label: "Procedure / reason for visit", type: "short_text", required: true },
    { key: "insurance_provider", label: "Insurance provider", type: "short_text" },
    { key: "insurance_member_id", label: "Insurance member ID", type: "identifier" },
    { key: "last_visit_date", label: "Date of last visit / cleaning", type: "date" },
  ],
};

/** Empty/blank starting values for a field-def list, keyed by field key. */
export function emptyCustomFieldValues(fields: IntakeFieldDef[]): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const f of fields) {
    values[f.key] = f.type === "boolean" ? false : f.type === "multi_select" ? [] : "";
  }
  return values;
}
